import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Effective permission keys for the current user (GET /permissions/me).
  // Server still enforces every action — this only drives client show/hide.
  const [permissions, setPermissions] = useState([]);

  const refreshPermissions = useCallback(async () => {
    try {
      const resp = await api.get('/permissions/me');
      setPermissions(resp.data?.permissions || []);
    } catch {
      setPermissions([]);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      refreshPermissions();
    }
    setLoading(false);
  }, [refreshPermissions]);

  const storeSession = useCallback(async ({ user, token }) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    await refreshPermissions();
    return user;
  }, [refreshPermissions]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    // Security audit 2026-09-01 §2.3: an MFA-enrolled account gets NO session
    // here — only a 5-minute mfa_token. The Login page collects the
    // authenticator code and calls completeMfaLogin.
    if (response.data?.mfa_required) {
      return { mfaRequired: true, mfaToken: response.data.mfa_token };
    }
    return storeSession(response.data);
  }, [storeSession]);

  const completeMfaLogin = useCallback(async (mfaToken, code) => {
    const response = await api.post('/auth/mfa/verify', { mfa_token: mfaToken, code });
    return storeSession(response.data);
  }, [storeSession]);

  const logout = useCallback(() => {
    // Security audit 2026-09-01 §2.MED.3: revoke THIS session server-side
    // (best-effort, fire-and-forget) before clearing local state — a copied
    // token no longer outlives the sign-out. Must be sent before the token is
    // removed from storage (the request interceptor reads it from there).
    const hadToken = !!localStorage.getItem('token');
    if (hadToken) {
      api.post('/auth/logout').catch(() => { /* already invalid / offline — local clear still happens */ });
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Never strand a stashed admin session behind a logout.
    localStorage.removeItem('impersonator_token');
    localStorage.removeItem('impersonator_user');
    setUser(null);
    setPermissions([]);
  }, []);

  // Support session (docs/specs/IMPERSONATION.md): swap to a short-lived token
  // for the target user. Their permissions apply everywhere; the admin session
  // is stashed and restored on exit (or by the 401 interceptor when the
  // support token expires). Full page reloads on both transitions so no stale
  // per-user React state leaks across the identity switch.
  const impersonate = useCallback(async (targetUserId) => {
    const res = await api.post('/admin/impersonate', { user_id: targetUserId });
    localStorage.setItem('impersonator_token', localStorage.getItem('token'));
    localStorage.setItem('impersonator_user', localStorage.getItem('user'));
    localStorage.setItem('token', res.data.token);
    // /auth/me with the support token returns the user WITH impersonated_by
    // set (the mint response's user object doesn't carry the marker).
    const me = await api.get('/auth/me');
    localStorage.setItem('user', JSON.stringify(me.data));
    window.location.href = '/';
  }, []);

  const exitImpersonation = useCallback(async () => {
    try {
      await api.post('/auth/impersonation/end'); // best-effort audit bracket
    } catch {
      /* token may already be expired — the restore below still applies */
    }
    const token = localStorage.getItem('impersonator_token');
    const savedUser = localStorage.getItem('impersonator_user');
    localStorage.removeItem('impersonator_token');
    localStorage.removeItem('impersonator_user');
    if (token) localStorage.setItem('token', token);
    else localStorage.removeItem('token');
    if (savedUser) localStorage.setItem('user', savedUser);
    else localStorage.removeItem('user');
    window.location.href = token ? '/admin' : '/login';
  }, []);

  const isDealershipUser = useCallback(() => user?.user_type === 'DealershipUser', [user]);
  const isVendorUser = useCallback(() => user?.user_type === 'VendorUser', [user]);
  const hasPermission = useCallback((key) => permissions.includes(key), [permissions]);

  // Memoized: this provider wraps the entire app, and a fresh value object on
  // every render forced every useAuth() consumer to re-render whenever the
  // provider did (perf review P28). Only real state changes bust it now.
  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      completeMfaLogin,
      logout,
      impersonate,
      exitImpersonation,
      isDealershipUser,
      isVendorUser,
      permissions,
      hasPermission,
      refreshPermissions,
    }),
    [user, loading, login, completeMfaLogin, logout, impersonate, exitImpersonation,
     isDealershipUser, isVendorUser, permissions, hasPermission, refreshPermissions],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};